import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledLabel, CheckboxIcon } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { capitalize } from "@helpers/general";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { mutate } from "swr";

import { setGlobalDataIsUpdated, setIsLoading } from "../store";
import { store } from "@components/VerticalLayout/store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../../api/hooks";

type ConfirmationModificationMessageType = {
    isModalOpened: boolean;
    setIsModalOpened: Function;
    setIsEdited: Function;
    setDataIsUpdated: Function;
    dataFiscalYear: any;
    associated: any[];
    isAddFiscalYearClicked: boolean;
    setIsAddFiscalYearClicked: Function;
};

export function ConfirmationModificationMessage({
    isModalOpened,
    setIsModalOpened,
    setIsEdited,
    setDataIsUpdated,
    dataFiscalYear,
    associated,
    isAddFiscalYearClicked,
    setIsAddFiscalYearClicked,
}: ConfirmationModificationMessageType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, operatorID } = useSnapshot(store);

    const [error, setError] = React.useState<boolean>(false);
    setIsLoading(true);
    async function editFiscalYear() {
        toast.dismiss();
        let objectData = {};
        let localData: any[] = [];

        associated.forEach(async (el: any) => {
            Object.assign(objectData, {
                [el.Id]: {
                    first_day: dataFiscalYear.data[el.Id].first_day,
                    first_month: dataFiscalYear.data[el.Id].first_month,
                    end_day: dataFiscalYear.data[el.Id].end_day,
                    end_month: dataFiscalYear.data[el.Id].end_month,
                    isArchived: el.isArchived,
                    shopId: el.shopId,
                },
            });

            let dataFiscalOfYear = {
                userId: userID,
                shopId: [el.shopId],
                franchiseId: franchiseID,
                operatorId: operatorID,
                data: objectData,
                isShop: true,
                isAssociate: true,
            };
            localData.push(
                fetchGlobal(
                    `${process.env.REACT_APP_API_V2_URL}/settings/general/fiscal_year`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(dataFiscalOfYear),
                    }
                )
            );
        });
        try {
            mutate(
                `${process.env.REACT_APP_API_V2_URL}/settings/general/fiscal_year`,
                await Promise.all(localData).then((result) => {
                    result.forEach((element: any) => {
                        if (element.error === true) setError(true);
                    });
                })
            );
        } catch (e) {
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }

        if (error === false) {
            toast.success(
                `${t("The fiscal year has been changed successfully")!}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
        }
        setDataIsUpdated(true);
        setIsAddFiscalYearClicked(!isAddFiscalYearClicked);
        setIsEdited(false);
        setIsLoading(false);
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsModalOpened(!isModalOpened);
                }}
            >
                <span
                    style={{
                        fontFamily: "Segoe UI Semibold",
                        color: "#2b2828",
                        fontSize: "20px",
                    }}
                >
                    {capitalize(t("Alert"))}
                </span>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        'Do you want to apply these changes for all stores? \n Attention ! If you click on "Validate", the specific settings of the shops will be modified.'
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                        editFiscalYear();
                        setGlobalDataIsUpdated(true);
                        setIsAddFiscalYearClicked(false);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
